import React, { useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Spinner from 'components/Spinner';
import axios from '../axios';

const AddCredit = () => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        address: '',
        amount: '',
        paymentPeriod: '',
        interestRate: '',
        creditType: 'Cash'
    });

    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
        setError(''); // Clear any errors when closing
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = () => {
        setLoading(true);
        setError(''); // Clear previous errors

        const { name, address, amount, paymentPeriod, interestRate, creditType } = formData;

        if (name && address && amount && paymentPeriod && interestRate && creditType) {
            const creditData = {
                ...formData,
                status: 'pending'
            };

            axios
                .post(`/credit/add-credit`, creditData)
                .then(() => {
                    setLoading(false);
                    // Reset form
                    setFormData({
                        name: '',
                        address: '',
                        amount: '',
                        paymentPeriod: '',
                        interestRate: '',
                        creditType: 'Cash'
                    });
                    setOpen(false);
                    window.location.href = '/credit';
                })
                .catch((error) => {
                    setError(
                        error.response?.data?.message || 'Failed to add credit. Please try again.'
                    );
                    setLoading(false);
                });
        } else {
            setError('Please fill in all required fields');
            setLoading(false);
        }
    };

    return (
        <div>
            <Button
                variant='contained'
                onClick={handleClickOpen}
                sx={{
                    backgroundColor: '#085d40',
                    color: '#ffffff',
                    fontWeight: 600,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontSize: '14px',
                    boxShadow: '0 2px 8px rgba(8, 93, 64, 0.2)',
                    '&:hover': {
                        backgroundColor: '#064532',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(8, 93, 64, 0.3)'
                    },
                    transition: 'all 0.2s ease'
                }}
            >
                Add Credit
            </Button>
            <Dialog
                fullScreen={fullScreen}
                open={open}
                onClose={handleClose}
                aria-labelledby='responsive-dialog-title'
                maxWidth='sm'
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: '16px',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                        backgroundColor: '#ffffff',
                        margin: '20px',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle
                    id='responsive-dialog-title'
                    sx={{
                        textAlign: 'center',
                        padding: '24px 24px 16px 24px',
                        backgroundColor: '#f8fafc',
                        color: '#1f2937',
                        fontSize: '20px',
                        fontWeight: 600,
                        borderBottom: '1px solid #e2e8f0',
                        margin: 0
                    }}
                >
                    Add New Credit
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: '32px 24px',
                        backgroundColor: '#ffffff'
                    }}
                >
                    <Box
                        component='form'
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3
                        }}
                        noValidate
                        autoComplete='off'
                    >
                        <TextField
                            required
                            id='credit-name'
                            label='Client Name'
                            name='name'
                            value={formData.name}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                },
                                marginTop: '20px'
                            }}
                        />
                        <TextField
                            required
                            id='credit-address'
                            label='Client Address'
                            name='address'
                            value={formData.address}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <TextField
                            required
                            id='credit-amount'
                            label='Credit Amount'
                            name='amount'
                            type='number'
                            value={formData.amount}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <TextField
                            required
                            id='credit-payment-period'
                            label='Payment Period (months)'
                            name='paymentPeriod'
                            type='number'
                            value={formData.paymentPeriod}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <TextField
                            required
                            id='credit-interest-rate'
                            label='Interest Rate (%)'
                            name='interestRate'
                            type='number'
                            value={formData.interestRate}
                            onChange={handleChange}
                            variant='outlined'
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#f9fafb',
                                    '&:hover': {
                                        backgroundColor: '#f3f4f6'
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#ffffff'
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#6b7280',
                                    fontWeight: 500
                                }
                            }}
                        />
                        <FormControl
                            component='fieldset'
                            sx={{
                                marginTop: '8px',
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb'
                            }}
                        >
                            <FormLabel
                                component='legend'
                                sx={{
                                    color: '#374151',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    marginBottom: '8px'
                                }}
                            >
                                Credit Type
                            </FormLabel>
                            <RadioGroup
                                row
                                aria-label='creditType'
                                name='creditType'
                                value={formData.creditType}
                                onChange={handleChange}
                                sx={{
                                    justifyContent: 'flex-start',
                                    gap: '16px'
                                }}
                            >
                                <FormControlLabel
                                    value='Cash'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#9ca3af',
                                                '&.Mui-checked': {
                                                    color: '#085d40'
                                                }
                                            }}
                                        />
                                    }
                                    label='Cash'
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '14px',
                                            color: '#374151',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                                <FormControlLabel
                                    value='Produce'
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#9ca3af',
                                                '&.Mui-checked': {
                                                    color: '#085d40'
                                                }
                                            }}
                                        />
                                    }
                                    label='Produce'
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '14px',
                                            color: '#374151',
                                            fontWeight: 500
                                        }
                                    }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    {error && (
                        <Box
                            sx={{
                                marginTop: 2,
                                padding: '12px 16px',
                                backgroundColor: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                color: '#dc2626',
                                fontSize: '14px',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            ⚠️ {error}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        padding: '20px 24px',
                        backgroundColor: '#f8fafc',
                        borderTop: '1px solid #e2e8f0',
                        gap: 2
                    }}
                >
                    <Button
                        onClick={handleClose}
                        variant='outlined'
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            fontWeight: 500,
                            padding: '10px 20px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            minWidth: '100px',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f9fafb'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: '100px',
                                justifyContent: 'center'
                            }}
                        >
                            <Spinner />
                        </Box>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            variant='contained'
                            sx={{
                                backgroundColor: '#085d40',
                                color: '#ffffff',
                                fontWeight: 600,
                                padding: '10px 20px',
                                borderRadius: '8px',
                                textTransform: 'none',
                                minWidth: '100px',
                                boxShadow: '0 2px 8px rgba(8, 93, 64, 0.2)',
                                '&:hover': {
                                    backgroundColor: '#064532',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 12px rgba(8, 93, 64, 0.3)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Add Credit
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AddCredit;
